import React from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import './Breadcrumb.css';

/**
 * Breadcrumb navigation component
 * Displays a hierarchical path of links
 * 
 * @param {Object[]} items - Array of breadcrumb items
 * @param {string} items[].label - Display text for the breadcrumb item
 * @param {string} items[].path - URL path for the breadcrumb item (optional for last item)
 * @param {number} maxItems - Maximum number of items to display (default: 4)
 *                          - When truncating, always shows first 2 items, ellipsis, then last (maxItems-3) items
 */
const Breadcrumb = ({ items, maxItems = 5 }) => {
  // Return null if no items are provided
  if (!items || items.length === 0) {
    return null;
  }

  // Apply max items limit with ellipsis for truncated items
  // Only apply truncation if we have enough items (at least 4)
  // and maxItems is at least 4 (to accommodate 2 at beginning, ellipsis, and at least 1 at end)
  const shouldTruncate = items.length > maxItems && items.length > 3 && maxItems >= 4;
  
  const displayItems = shouldTruncate
    ? [
        ...items.slice(0, 2), // Always show first two items
        { label: '...', path: null, className: 'breadcrumb__item--ellipsis' }, // Ellipsis indicator
        ...items.slice(items.length - (maxItems - 3)) // Show last items (adjusted for 2 items at start + ellipsis)
      ]
    : items;

  return (
    <nav className="breadcrumb" aria-label="Breadcrumb">
      <ol className="breadcrumb__list">
        {displayItems.map((item, index) => {
          const isLast = index === displayItems.length - 1;
          
          return (
            <li 
              key={index} 
              className={`breadcrumb__item ${isLast ? 'breadcrumb__item--active' : ''} ${item.className || ''}`}
              aria-current={isLast ? 'page' : undefined}
            >
              {item.path && !isLast ? (
                <Link to={item.path} className="breadcrumb__link">
                  {item.label}
                </Link>
              ) : (
                <span className="breadcrumb__text">
                  {item.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

Breadcrumb.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      path: PropTypes.string,
      className: PropTypes.string
    })
  ).isRequired,
  maxItems: PropTypes.number
};

export default Breadcrumb;